import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  LayoutGrid,
  Search,
  FileText,
  Users,
  CheckCircle,
  MessageSquare,
  Target,
  CreditCard,
  User,
  Settings,
  HelpCircle,
  LogOut,
  Menu,
  X,
  ChevronsLeft,
  ChevronsRight
} from "lucide-react";
import { useSidebar } from "@/contexts/SidebarContext";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import turboChatService from "@/utils/turboChatService";
import NotificationDropdown from "@/components/NotificationDropdown";

/* --- Command Deck colour-tinted icon-chip palette ------------------------- */
const TINTS = {
  aqua:    { bg: "rgba(40,192,208,.16)",  color: "#28C0D0" },
  mint:    { bg: "rgba(103,198,180,.16)", color: "#67C6B4" },
  magenta: { bg: "rgba(120,40,124,.22)",  color: "#C98AD4" },
  blue:    { bg: "rgba(96,165,250,.16)",  color: "#60A5FA" },
  slate:   { bg: "rgba(255,255,255,.08)", color: "rgba(255,255,255,.7)" },
};
const ACTIVE_CHIP = "rgba(40,192,208,.2)";
const ACTIVE_ICON = "#28C0D0";

const Sidebar = ({ onLogout }) => {
  const location = useLocation();
  const { isCollapsed, toggleCollapsed } = useSidebar();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [supportEnabled, setSupportEnabled] = useState(true);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const isAdminMode = !!localStorage.getItem('admin_viewing_as_user');
  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');

  const displayName = user.full_name || user.fullName ||
    `${user.first_name || user.firstName || ''} ${user.last_name || user.lastName || ''}`.trim() || 'User';
  const avatarInitial = (displayName.trim()[0] || 'U').toUpperCase();
  const roleLine = user.business_name || (user.role ? `Crew · ${user.role}` : 'Crew Member');

  // Load support enabled setting from backend (public endpoint)
  useEffect(() => {
    const loadSupportSetting = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/settings`);
        if (response.data && response.data.support_enabled !== undefined) {
          setSupportEnabled(response.data.support_enabled);
        }
      } catch (error) {
        console.log('Could not load support setting, defaulting to enabled');
      }
    };
    loadSupportSetting();
  }, []);

  // Fetch unread message count and poll for updates
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user.email) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/messages/unread-count?user_email=${user.email}`
        );
        setUnreadMessageCount(response.data.unread_count || 0);
      } catch (error) {
        console.log('Could not load unread count');
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000);
    return () => clearInterval(interval);
  }, [user.email]);

  // Handle support chat opening
  const handleSupportClick = () => {
    turboChatService.open();
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMobileMenuOpen && !e.target.closest('.mobile-sidebar') && !e.target.closest('.hamburger-button')) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobileMenuOpen]);

  const menuItems = [
    { group: "MENU", path: "/dashboard", icon: LayoutGrid, label: "Dashboard", tint: "aqua" },
    { group: "MENU", path: "/find-jobs", icon: Search, label: "Find Jobs", tint: "aqua" },
    { group: "MENU", path: "/applications", icon: FileText, label: "My Applications", tint: "mint" },
    { group: "MENU", path: "/hire-crew", icon: Users, label: "Hire Crew", tint: "magenta" },
    { group: "MENU", path: "/job-comms", icon: CheckCircle, label: "Confirmed Jobs", tint: "blue" },
    { group: "ACTIVITY", path: "/messages", icon: MessageSquare, label: "Messages", tint: "magenta" },
    { group: "ACTIVITY", path: "/leads", icon: Target, label: "Leads", tint: "aqua" },
    { group: "ACTIVITY", path: "/invoices", icon: CreditCard, label: "Invoices", tint: "mint" },
    { group: "ACTIVITY", path: "/profile", icon: User, label: "Profile", tint: "slate" },
  ];

  const isItemActive = (path) =>
    location.pathname === path || (path === '/job-comms' && location.pathname.startsWith('/job-comms'));

  // --- Single nav-item renderer (handles active/inactive + collapsed) -------
  const renderNavItem = (item) => {
    const Icon = item.icon;
    const isActive = isItemActive(item.path);
    const tint = TINTS[item.tint] || TINTS.slate;
    const showBadge = item.path === '/messages' && unreadMessageCount > 0;

    const itemStyle = isActive
      ? {
          background: '#0A0E1A',
          border: '1px solid rgba(255,255,255,.1)',
          boxShadow: '0 10px 24px -12px rgba(0,0,0,.8)',
          color: '#fff',
        }
      : {
          background: 'transparent',
          border: '1px solid transparent',
          color: 'rgba(255,255,255,.62)',
        };

    const chipStyle = {
      background: isActive ? ACTIVE_CHIP : tint.bg,
      color: isActive ? ACTIVE_ICON : tint.color,
    };

    const link = (
      <Link
        key={item.path}
        to={item.path}
        data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        className={`group flex items-center rounded-[13px] transition-colors ${
          isCollapsed
            ? 'lg:justify-center lg:gap-0 lg:mx-1 lg:px-0 lg:py-2 gap-3 px-2.5 py-1'
            : 'gap-3 px-2.5 py-1'
        }`}
        style={{
          ...itemStyle,
          fontFamily: 'var(--sz-font-body)',
          fontSize: '13.5px',
          fontWeight: isActive ? 700 : 500,
        }}
      >
        <span
          className="relative flex items-center justify-center flex-shrink-0 rounded-[9px]"
          style={{ width: 32, height: 32, ...chipStyle }}
        >
          <Icon className="w-[17px] h-[17px]" strokeWidth={2} />
          {showBadge && (
            <span
              className="absolute -top-1.5 -right-1.5 flex items-center justify-center rounded-full px-1"
              style={{
                minWidth: 18, height: 18,
                background: '#78287C', color: '#fff',
                fontFamily: 'var(--sz-font-display)', fontWeight: 700, fontSize: '10px',
              }}
            >
              {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
            </span>
          )}
        </span>
        <span className={isCollapsed ? 'lg:hidden' : ''}>{item.label}</span>
        {/* Messages count chip inline (expanded only) */}
        {showBadge && (
          <span
            className={`ml-auto flex items-center justify-center rounded-full px-1.5 ${isCollapsed ? 'lg:hidden' : ''}`}
            style={{
              minWidth: 20, height: 20,
              background: '#78287C', color: '#fff',
              fontFamily: 'var(--sz-font-display)', fontWeight: 700, fontSize: '10.5px',
            }}
          >
            {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
          </span>
        )}
      </Link>
    );

    if (isCollapsed) {
      return (
        <Tooltip key={item.path}>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right" className="hidden lg:block">
            <p className="font-medium">{item.label}</p>
          </TooltipContent>
        </Tooltip>
      );
    }
    return link;
  };

  const groupHeadingStyle = {
    fontFamily: 'var(--sz-font-display)',
    fontWeight: 700,
    fontSize: '10px',
    letterSpacing: '1.5px',
    color: 'rgba(255,255,255,.3)',
  };

  const menuGroup = menuItems.filter((i) => i.group === 'MENU');
  const activityGroup = menuItems.filter((i) => i.group === 'ACTIVITY');

  // Footer "slate" link style (Settings / Support)
  const footerLinkClass = `group flex items-center rounded-[13px] transition-colors text-[rgba(255,255,255,.62)] hover:text-white ${
    isCollapsed ? 'lg:justify-center lg:gap-0 lg:px-0 lg:py-2 gap-3 px-2.5 py-1' : 'gap-3 px-2.5 py-1'
  }`;
  const slateChipStyle = { width: 27, height: 27, background: TINTS.slate.bg, color: TINTS.slate.color };

  return (
    <TooltipProvider delayDuration={0}>
      {/* Mobile Hamburger Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsMobileMenuOpen(!isMobileMenuOpen);
        }}
        className="hamburger-button fixed left-2 z-[60] lg:hidden p-3 rounded-xl shadow-xl text-white active:scale-95 transition-transform"
        style={{
          top: isAdminMode ? 'calc(0.5rem + 40px)' : '0.5rem',
          touchAction: 'manipulation',
          background: 'rgba(10,14,26,.85)',
          border: '1px solid rgba(255,255,255,.12)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile top bar — centered white Shootzu logo (hidden when drawer open) */}
      {!isMobileMenuOpen && (
        <div
          className="fixed left-0 right-0 z-[55] lg:hidden flex items-center justify-center"
          style={{
            top: isAdminMode ? '40px' : '0',
            height: '58px',
            background: 'rgba(10,14,26,.55)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255,255,255,.06)',
            pointerEvents: 'none',
          }}
        >
          <img src="/assets/shootzu-logo-white.png" alt="Shootzu" style={{ height: 30, width: 'auto' }} />
        </div>
      )}

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" style={{ background: 'rgba(12,17,32,.55)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' }} />
      )}

      {/* Sidebar */}
      <div
        className={`mobile-sidebar fixed left-0 w-72 flex flex-col z-50 transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${isCollapsed ? 'lg:w-16' : 'lg:w-60'}`}
        style={{
          top: isAdminMode ? '40px' : '0',
          height: isAdminMode ? 'calc(100% - 40px)' : '100%',
          background: 'rgba(10,14,26,.55)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRight: '1px solid rgba(255,255,255,.08)',
          fontFamily: 'var(--sz-font-body)',
        }}
      >
        {/* Logo — centered on mobile (clears the floating X button); left-aligned on desktop */}
        <div className={`relative flex items-center ${isCollapsed ? 'lg:justify-center lg:px-2' : 'lg:justify-between px-5'}`} style={{ padding: isCollapsed ? undefined : '22px 20px 14px', minHeight: 64 }}>
          {isCollapsed ? (
            <img src="/assets/shootzu-icon.png" alt="Shootzu" className="hidden lg:block w-9 h-9 object-contain" />
          ) : null}
          <img
            src="/assets/shootzu-logo-white.png"
            alt="Shootzu"
            className={`${isCollapsed ? 'lg:hidden' : ''} w-auto object-contain mx-auto lg:mx-0`}
            style={{ height: 38 }}
          />
          {/* Notifications bell (global header bar was removed) */}
          {!isCollapsed && (
            <div className="flex-shrink-0 absolute right-4 lg:static lg:right-auto">
              <NotificationDropdown />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto flex flex-col gap-[2px]" style={{ padding: '6px 12px' }}>
          {/* MENU group */}
          <div className={`px-3 pt-2 pb-1.5 ${isCollapsed ? 'lg:hidden' : ''}`} style={groupHeadingStyle}>MENU</div>
          {menuGroup.map(renderNavItem)}

          {/* ACTIVITY group */}
          <div className={`px-3 pb-1.5 ${isCollapsed ? 'lg:hidden' : ''}`} style={{ ...groupHeadingStyle, paddingTop: '16px' }}>ACTIVITY</div>
          {activityGroup.map(renderNavItem)}
        </nav>

        {/* Footer: Settings, Support, user chip + logout */}
        <div className="flex flex-col gap-[2px]" style={{ padding: '10px 12px 14px', borderTop: '1px solid rgba(255,255,255,.07)' }}>
          {/* Settings */}
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/settings" data-testid="settings-button" className={footerLinkClass} style={{ fontSize: '12.5px', fontWeight: 500, paddingTop: 6, paddingBottom: 6 }}>
                  <span className="flex items-center justify-center flex-shrink-0 rounded-[9px]" style={slateChipStyle}>
                    <Settings className="w-[17px] h-[17px]" strokeWidth={2} />
                  </span>
                  <span className="lg:hidden">Settings</span>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" className="hidden lg:block">Settings</TooltipContent>
            </Tooltip>
          ) : (
            <Link to="/settings" data-testid="settings-button" className={footerLinkClass} style={{ fontSize: '12.5px', fontWeight: 500, paddingTop: 6, paddingBottom: 6 }}>
              <span className="flex items-center justify-center flex-shrink-0 rounded-[9px]" style={slateChipStyle}>
                <Settings className="w-[17px] h-[17px]" strokeWidth={2} />
              </span>
              <span>Settings</span>
            </Link>
          )}

          {/* Support */}
          {supportEnabled && (
            isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={handleSupportClick} data-testid="support-button" className={footerLinkClass} style={{ fontSize: '12.5px', fontWeight: 500, paddingTop: 6, paddingBottom: 6 }}>
                    <span className="flex items-center justify-center flex-shrink-0 rounded-[9px]" style={slateChipStyle}>
                      <HelpCircle className="w-[17px] h-[17px]" strokeWidth={2} />
                    </span>
                    <span className="lg:hidden">Support</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="hidden lg:block">Support</TooltipContent>
              </Tooltip>
            ) : (
              <button onClick={handleSupportClick} data-testid="support-button" className={footerLinkClass} style={{ fontSize: '12.5px', fontWeight: 500, paddingTop: 6, paddingBottom: 6 }}>
                <span className="flex items-center justify-center flex-shrink-0 rounded-[9px]" style={slateChipStyle}>
                  <HelpCircle className="w-[17px] h-[17px]" strokeWidth={2} />
                </span>
                <span>Support</span>
              </button>
            )
          )}

          {/* User chip + logout */}
          <div className={`flex items-center justify-between ${isCollapsed ? 'lg:flex-col lg:gap-2' : ''}`} style={{ padding: '14px 11px 4px' }}>
            <div className={`flex items-center gap-2 min-w-0 ${isCollapsed ? 'lg:hidden' : ''}`}>
              <a href="https://apps.apple.com/au/app/shootzu/id6756085766" target="_blank" rel="noopener noreferrer" title="Download on the App Store"
                 className="flex items-center justify-center rounded-[9px] transition-colors hover:text-white"
                 style={{ width: 34, height: 34, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)', color: 'rgba(255,255,255,.85)' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16.365 1.43c0 1.14-.42 2.2-1.12 3.01-.84.97-2.2 1.72-3.36 1.62-.14-1.1.42-2.27 1.08-3.01.78-.86 2.2-1.55 3.4-1.62zM20.5 17.06c-.55 1.27-.82 1.83-1.53 2.95-.99 1.56-2.39 3.51-4.12 3.52-1.54.01-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.01-3.05-1.77-4.04-3.33-2.77-4.37-3.06-9.5-1.35-12.22 1.21-1.93 3.13-3.06 4.93-3.06 1.84 0 2.99 1.01 4.51 1.01 1.47 0 2.37-1.01 4.5-1.01 1.6 0 3.3.87 4.51 2.38-3.96 2.17-3.32 7.83.21 9.79z"/></svg>
              </a>
              <a href="https://play.google.com/store/apps/details?id=au.com.shootzu.app" target="_blank" rel="noopener noreferrer" title="Get it on Google Play"
                 className="flex items-center justify-center rounded-[9px] transition-colors hover:text-white"
                 style={{ width: 34, height: 34, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)', color: 'rgba(255,255,255,.85)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3.3 2.2A1 1 0 0 0 3 2.9v18.2a1 1 0 0 0 .3.7l10-9.8-10-9.8z"/><path d="M16.8 8.9 5.8 2.6l8.4 8.2 2.6-1.9z"/><path d="m14.2 13.2-8.4 8.2 11-6.3-2.6-1.9z"/><path d="m17.9 9.6-2.8 2 2.8 2 3-1.7c.7-.4.7-1.3 0-1.7l-3-1.6z"/></svg>
              </a>
            </div>
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={onLogout} data-testid="logout-button" className="flex-shrink-0 p-1.5 rounded-lg transition-colors" style={{ color: 'rgba(255,255,255,.5)' }}>
                    <LogOut className="w-[17px] h-[17px]" strokeWidth={2} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="hidden lg:block">Logout</TooltipContent>
              </Tooltip>
            ) : (
              <button onClick={onLogout} data-testid="logout-button" className="flex-shrink-0 p-1.5 rounded-lg transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,.5)' }} title="Logout">
                <LogOut className="w-[17px] h-[17px]" strokeWidth={2} />
              </button>
            )}
          </div>
        </div>

        {/* Collapse Toggle - Desktop only, at very bottom */}
        <div className={`hidden lg:flex items-center ${isCollapsed ? 'justify-center px-2 py-2' : 'justify-end px-3 py-2'}`} style={{ borderTop: '1px solid rgba(255,255,255,.07)' }}>
          <button
            onClick={toggleCollapsed}
            className="p-1.5 rounded-lg transition-colors hover:text-white"
            style={{ color: 'rgba(255,255,255,.45)' }}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default Sidebar;
